// Dynamic background component that displays the latest background media item

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useRoomMediaElements } from '../../../hooks';
import { cn } from '../../../utilities/cn';
import { MediaType } from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('Background');

export default function Background() {
  const [currentBackgroundUrl, setCurrentBackgroundUrl] = useState<
    string | null
  >(null);
  const [previousBackgroundUrl, setPreviousBackgroundUrl] = useState<
    string | null
  >(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [queuedBackground, setQueuedBackground] = useState<string | null>(null);

  // Use room media queries to get the latest background
  const { getLastMediaElement } = useRoomMediaElements();

  // Memoize the latest background to avoid unnecessary re-renders
  const latestBackground = useMemo(
    () => getLastMediaElement(MediaType.BACKGROUND),
    [getLastMediaElement]
  );

  // Shared function to start background transitions
  const startBackgroundTransition = useCallback(
    (newUrl: string, logMessage?: string) => {
      setIsTransitioning(true);
      setPreviousBackgroundUrl(currentBackgroundUrl);
      setCurrentBackgroundUrl(newUrl);

      if (logMessage) {
        logger.info(logMessage);
      }

      // Trigger fade-in on next frame
      requestAnimationFrame(() => {
        setIsTransitioning(false);
      });

      // Clean up previous background after transition
      setTimeout(() => {
        setPreviousBackgroundUrl(null);
      }, 2000);
    },
    [currentBackgroundUrl]
  );

  // Find the latest background and update URL when it changes
  useEffect(() => {
    const imageUrl = latestBackground?.imageUrl;

    // Clear background if no element or URL exists
    if (!imageUrl) {
      if (currentBackgroundUrl !== null) {
        setCurrentBackgroundUrl(null);
      }
      setQueuedBackground(null); // Clear any queued background
      return;
    }

    // Skip if no change needed
    if (imageUrl === currentBackgroundUrl) {
      return;
    }

    // If already transitioning, queue this background for later
    if (isTransitioning) {
      logger.debug(
        'Background transition in progress, queueing new background:',
        latestBackground.mediaId
      );
      setQueuedBackground(imageUrl);
      return;
    }

    // Start transition to new background
    setQueuedBackground(null); // Clear any previously queued background
    startBackgroundTransition(
      imageUrl,
      `Background changed: ${latestBackground.mediaId}`
    );
  }, [
    latestBackground,
    currentBackgroundUrl,
    isTransitioning,
    startBackgroundTransition,
  ]);

  // Process queued backgrounds when transition completes
  useEffect(() => {
    // If we have a queued background and we're not transitioning, process it
    if (
      queuedBackground &&
      !isTransitioning &&
      queuedBackground !== currentBackgroundUrl
    ) {
      logger.debug('Processing queued background:', queuedBackground);

      // Start transition to queued background
      setQueuedBackground(null); // Clear the queue
      startBackgroundTransition(queuedBackground);
    }
  }, [
    queuedBackground,
    isTransitioning,
    currentBackgroundUrl,
    startBackgroundTransition,
  ]);

  // Don't render anything if no background URL is available
  if (!currentBackgroundUrl) {
    return null;
  }

  const backgroundClassName =
    'pointer-events-none fixed inset-0 z-0 bg-cover bg-center bg-no-repeat transition-opacity duration-[2000ms] ease-linear';

  return (
    <>
      {/* Previous background (fading out) */}
      {previousBackgroundUrl && (
        <div
          className={cn(
            backgroundClassName,
            isTransitioning ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            backgroundImage: `url(${previousBackgroundUrl})`,
          }}
          aria-hidden="true"
        />
      )}

      {/* Current background (fading in) */}
      <div
        className={cn(
          backgroundClassName,
          isTransitioning ? 'opacity-0' : 'opacity-100'
        )}
        style={{
          backgroundImage: `url(${currentBackgroundUrl})`,
        }}
        aria-hidden="true"
      />
    </>
  );
}
